/**
 * Uncaught errors in the staff app, sent to the server's log (POST /client-errors),
 * where support can find them by person and correlation id. Never stored there.
 */

export interface ClientErrorReport {
  kind: 'error' | 'rejection'
  message: string
  source?: string
  line?: number
  column?: number
  stack?: string
  page: string
}

let signedIn = false

/** Whether somebody is signed in, so a report has a session to go with; set by App. */
export function setClientErrorsSignedIn(on: boolean): void {
  signedIn = on
}

/** Whether a report may be sent now. */
export const clientErrorsEnabled = (): boolean => signedIn

/** At most this many reports a minute leave one tab; the server limits per session too. */
export const CLIENT_ERRORS_PER_MINUTE = 5

const clip = (s: unknown, n: number) => String(s ?? '').slice(0, n)

/** The report for an error event or an unhandled rejection's reason. */
export function reportOf(kind: ClientErrorReport['kind'], reason: unknown, page: string, at?: { source?: string; line?: number; column?: number }): ClientErrorReport {
  const err = reason instanceof Error ? reason : undefined
  return {
    kind,
    message: clip(err ? `${err.name}: ${err.message}` : reason, 500),
    source: clip(at?.source, 300),
    line: at?.line ?? 0,
    column: at?.column ?? 0,
    stack: clip(err?.stack, 2000),
    page: clip(page.split(/[?#]/)[0], 300),
  }
}

/**
 * Listens for uncaught errors and unhandled rejections on target and sends each
 * through send: the same message once a minute, and no more than the limit. A
 * failed send is dropped, never reported in turn. Returns the way to stop.
 */
export function reportClientErrors(
  target: Pick<Window, 'addEventListener' | 'removeEventListener'>,
  send: (report: ClientErrorReport) => Promise<unknown>,
  page: () => string,
  now: () => number = Date.now,
  enabled: () => boolean = clientErrorsEnabled,
): () => void {
  let windowStart = -Infinity
  let sent = 0
  const seen = new Set<string>()
  const offer = (report: ClientErrorReport) => {
    if (!enabled()) return
    const t = now()
    if (t - windowStart >= 60_000) {
      windowStart = t
      sent = 0
      seen.clear()
    }
    if (sent >= CLIENT_ERRORS_PER_MINUTE || seen.has(report.message)) return
    sent++
    seen.add(report.message)
    send(report).catch(() => undefined)
  }
  const onError = (e: Event) => {
    const ev = e as ErrorEvent
    offer(reportOf('error', ev.error ?? ev.message, page(), { source: ev.filename, line: ev.lineno, column: ev.colno }))
  }
  const onRejection = (e: Event) => offer(reportOf('rejection', (e as PromiseRejectionEvent).reason, page()))
  target.addEventListener('error', onError)
  target.addEventListener('unhandledrejection', onRejection)
  return () => {
    target.removeEventListener('error', onError)
    target.removeEventListener('unhandledrejection', onRejection)
  }
}
